import { Bold, Italic, List, ListOrdered, Strikethrough, Underline } from "lucide-react";
import UnderlineExtension from "@tiptap/extension-underline";
import { EditorProvider, useCurrentEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "randmarcomps";
import "./styles.css";

const extensions = [StarterKit, UnderlineExtension];

const MenuBar = () => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  const activeBlockType = (() => {
    if (editor.isActive("heading")) {
      const level = editor.getAttributes("heading")?.level as number | undefined;
      if (typeof level === "number" && level >= 1 && level <= 6) return `h${level}`;
    }
    return "paragraph";
  })();

  const toggleButtonClassName = (isActive: boolean) =>
    [
      "h-9 w-9 rounded-lg border border-gray-200 bg-white p-0 shadow-sm hover:bg-gray-50",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]",
      isActive ? "border-[var(--ops-border-strong)] bg-[var(--ops-accent-muted)] text-[color:var(--ops-accent)]" : "text-gray-700",
    ].join(" ");

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={activeBlockType}
          onValueChange={(value) => {
            if (value === "paragraph") {
              editor.chain().focus().setParagraph().run();
              return;
            }
            const level = value === "h1" ? 1 : value === "h2" ? 2 : value === "h3" ? 3 : undefined;
            if (!level) return;
            editor.chain().focus().setHeading({ level }).run();
          }}
        >
          <SelectTrigger className="h-9 w-[160px] rounded-lg border border-gray-200 bg-white text-sm shadow-sm">
            <SelectValue placeholder="Text style" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="paragraph">Paragraph</SelectItem>
            <SelectItem value="h1">Heading 1</SelectItem>
            <SelectItem value="h2">Heading 2</SelectItem>
            <SelectItem value="h3">Heading 3</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className={toggleButtonClassName(editor.isActive("bold"))}
            onClick={() => editor.chain().focus().toggleBold().run()}
            aria-label="Toggle bold"
          >
            <Bold className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className={toggleButtonClassName(editor.isActive("italic"))}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            aria-label="Toggle italic"
          >
            <Italic className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className={toggleButtonClassName(editor.isActive("underline"))}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            aria-label="Toggle underline"
          >
            <Underline className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className={toggleButtonClassName(editor.isActive("strike"))}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            aria-label="Toggle strikethrough"
          >
            <Strikethrough className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className={toggleButtonClassName(editor.isActive("bulletList"))}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            aria-label="Toggle bullet list"
          >
            <List className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className={toggleButtonClassName(editor.isActive("orderedList"))}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            aria-label="Toggle numbered list"
          >
            <ListOrdered className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
      <div className="my-3 h-px w-full bg-gray-100" />
    </>
  );
};

interface RichTextEditorProps {
  initialContent: string;
  setContent: (content: string) => void;
}

const RichTextEditor = ({ initialContent, setContent }: RichTextEditorProps) => {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm focus-within:ring-2 focus-within:ring-[var(--ops-focus-ring)]">
      <EditorProvider
        slotBefore={<MenuBar />}
        extensions={extensions}
        content={initialContent}
        onUpdate={({ editor }) => setContent(editor.getHTML())}
        editorProps={{
          attributes: {
            class: "tiptap px-3 py-2 text-[15px] text-gray-800",
          },
        }}
      >
        <></>
      </EditorProvider>
    </div>
  );
};

export default RichTextEditor;
